import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import type { DataTableStateEvent } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import type { PaginatorTemplateOptions } from "primereact/paginator";
import { Toast } from "primereact/toast";

import { EMBARCACOES_TABLE_DENSITY_KEY } from "@/core/constants/keys";
import { Paths } from "@/core/constants/paths";
import { clearAuthToken } from "@/modules/auth";
import {
  DEFAULT_EMBARCACOES_SORT,
  EMBARCACAO_FILTER_COLUMNS,
  EMBARCACAO_FILTER_LABELS,
  EMPTY_EMBARCACOES_FILTERS,
  hasEmbarcacoesFilters,
  isEmbarcacoesSortField,
  useEmbarcacaoFilterOptions,
  useEmbarcacoes,
} from "@/modules/embarcacoes";
import type { Embarcacao, EmbarcacoesFilters, EmbarcacoesSort } from "@/modules/embarcacoes";
import { EmptyState, resolveEmptyStateKind } from "@/screens/control-panel/components/empty-state";
import type { EmptyStateContent } from "@/screens/control-panel/components/empty-state";
import { StateMessage } from "@/screens/control-panel/components/state-message";
import { FilterPanel } from "@/shared/components/filter-panel";
import type { FilterPanelColumn, FilterPanelValue } from "@/shared/components/filter-panel";
import { EmbarcacaoDetailDialog } from "./components/embarcacao-detail-dialog";
import { EmbarcacaoRowActions } from "./components/embarcacao-row-actions";
import { TableSettingsDialog, isEmbarcacoesDensity } from "./components/table-settings-dialog";
import type {
  EmbarcacoesDensity,
  EmbarcacoesTableSettings,
} from "./components/table-settings-dialog";
import styles from "./embarcacoes-screen.module.scss";

export const EMBARCACOES_TITLE = "Embarcações";
export const SEARCH_PLACEHOLDER = "Buscar Embarcação";
export const TABLE_SETTINGS_APPLIED_MESSAGE = "Configurações da tabela atualizada com sucesso.";

/**
 * "Exportar Visualização" é apenas o ponto de entrada previsto nos Padrões
 * Globais de Interface (AC4): a exportação da listagem é história própria, então
 * o item aparece no menu ⋮ sem executar nada nesta entrega.
 */
export const EXPORT_UNAVAILABLE_TITLE = "Disponível em uma próxima entrega.";

/** Listagem vazia: ainda não há embarcações para exibir. */
const EMPTY_STATE: EmptyStateContent = {
  title: "Nenhuma embarcação cadastrada",
  description: "Assim que houver embarcações disponíveis, elas aparecerão nesta listagem.",
};

/** Busca sem resultados — `ilu-no-search` do Figma (AC2). */
const EMPTY_SEARCH_STATE: EmptyStateContent = {
  title: "Nenhuma embarcação corresponde à busca.",
  description: "Tente revisar o termo pesquisado",
};

/** Filtros sem resultados — `ilu-no-filters` do Figma (AC3). */
const EMPTY_FILTER_STATE: EmptyStateContent = {
  title: "Nenhuma embarcação corresponde aos filtros selecionados.",
  description: "Tente ajustar ou limpar os filtros",
};

/**
 * O contrato da HU 812 traz `pageSize` 20 como padrão do serviço, mas as tabelas
 * do Hub Web oferecem 10/25/50 — a tela segue o padrão das demais listagens e
 * informa o tamanho escolhido em toda consulta.
 */
const DEFAULT_ROWS_PER_PAGE = 10;

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const ROWS_PER_PAGE_DROPDOWN_OPTIONS = ROWS_PER_PAGE_OPTIONS.map((rows) => ({
  label: `${rows} por página`,
  value: rows,
}));

/** AC1 — o rodapé informa o intervalo exibido e o total de registros. */
const CURRENT_PAGE_REPORT = "Mostrando {first} a {last} de {totalRecords} registros";

const COLUMN_WIDTHS: Record<string, CSSProperties> = {
  acronimo: { width: "8.4375rem" },
  nome: { minWidth: "18rem" },
  tipo: { minWidth: "12rem" },
  situacao: { width: "8.4375rem" },
  acoes: { width: "3.25rem" },
};

/** A coluna Embarcação é o campo `nome` do contrato. */
const SORT_FIELD_BY_COLUMN = {
  acronimo: "acronimo",
  nome: "nome",
  tipo: "tipo",
  situacao: "situacao",
} as const;

/** A densidade escolhida acompanha o usuário entre visitas, como nas demais listagens. */
function readStoredDensity(): EmbarcacoesDensity {
  try {
    const stored = localStorage.getItem(EMBARCACOES_TABLE_DENSITY_KEY);
    return isEmbarcacoesDensity(stored) ? stored : "confortavel";
  } catch {
    return "confortavel";
  }
}

function persistDensity(value: EmbarcacoesDensity) {
  try {
    localStorage.setItem(EMBARCACOES_TABLE_DENSITY_KEY, value);
  } catch {
    return;
  }
}

const PAGINATOR_TEMPLATE: PaginatorTemplateOptions = {
  layout:
    "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
  RowsPerPageDropdown: (options) => (
    <Dropdown
      // Mantém a classe do template padrão para o tema alcançar o seletor.
      className="p-paginator-rpp-options"
      panelClassName={styles.rowsPerPagePanel}
      value={options.value}
      options={ROWS_PER_PAGE_DROPDOWN_OPTIONS}
      onChange={options.onChange}
      aria-label="Embarcações por página"
      data-testid="embarcacoes-rows-per-page"
    />
  ),
};

/**
 * Colunas filtráveis do painel, cada uma ligada ao serviço de opções da sua
 * coluna. Um hook por coluna mantém o carregamento independente: uma lista que
 * falha não derruba as demais.
 */
function useEmbarcacaoFilterColumns(): FilterPanelColumn[] {
  const acronimo = useEmbarcacaoFilterOptions("acronimo");
  const embarcacao = useEmbarcacaoFilterOptions("embarcacao");
  const tipo = useEmbarcacaoFilterOptions("tipo");
  const situacao = useEmbarcacaoFilterOptions("situacao");

  return useMemo(() => {
    const states = { acronimo, embarcacao, tipo, situacao };

    return EMBARCACAO_FILTER_COLUMNS.map((key) => {
      const label = EMBARCACAO_FILTER_LABELS[key];

      return {
        key,
        label,
        options: states[key].options,
        isLoading: states[key].isLoading,
        hasError: states[key].hasError,
        // AC3 — o contrato recebe listas: cada coluna aceita vários valores.
        mode: "multiple" as const,
        searchPlaceholder: `Buscar ${label.toLowerCase()}`,
      };
    });
  }, [acronimo, embarcacao, tipo, situacao]);
}

/**
 * Listagem de Embarcações (US-1790) — consulta somente leitura da frota (RN003):
 * busca, filtros no novo padrão, paginação no servidor e a lupa de cada linha,
 * que abre o detalhamento da embarcação sobre a listagem (US-1791).
 */
export function EmbarcacoesScreen() {
  const navigate = useNavigate();
  const configPanel = useRef<OverlayPanel>(null);
  const toast = useRef<Toast>(null);
  /**
   * Lupa que abriu o detalhamento: ao fechar o modal o foco volta para ela,
   * na linha de origem (AC1).
   */
  const detailTrigger = useRef<HTMLElement | null>(null);

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<EmbarcacoesFilters>(EMPTY_EMBARCACOES_FILTERS);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(DEFAULT_ROWS_PER_PAGE);
  // `null` é o terceiro estado do ciclo — sem ordenação escolhida, a listagem
  // segue na ordem padrão da tela e nenhum cabeçalho exibe a seta.
  const [sort, setSort] = useState<EmbarcacoesSort | null>(null);
  const [settings, setSettings] = useState<EmbarcacoesTableSettings>(() => ({
    density: readStoredDensity(),
  }));
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  // O detalhamento vive em estado da tela: o id não trafega na URL (AC1).
  const [detailId, setDetailId] = useState<string | null>(null);

  const filterColumns = useEmbarcacaoFilterColumns();

  const { embarcacoes, total, status, errorMessage, reload } = useEmbarcacoes({
    search,
    filters,
    page: Math.floor(first / rows) + 1,
    pageSize: rows,
    sort: sort ?? DEFAULT_EMBARCACOES_SORT,
  });

  const isLoading = status === "loading";
  const hasSearch = search.trim() !== "";
  const hasFilters = hasEmbarcacoesFilters(filters);
  const emptyKind = resolveEmptyStateKind(hasSearch, hasFilters);
  const emptyContent =
    emptyKind === "search"
      ? EMPTY_SEARCH_STATE
      : emptyKind === "filter"
        ? EMPTY_FILTER_STATE
        : EMPTY_STATE;

  // Só depois da consulta concluída: enquanto ela corre, a tabela segue em tela
  // com o indicador de carregamento e os registros da consulta anterior.
  const isEmpty = status === "success" && embarcacoes.length === 0;

  const sortOrder = sort ? (sort.direction === "asc" ? 1 : -1) : null;

  // Um total menor (nova busca, novo filtro, recarga) pode deixar a página atual fora da faixa.
  useEffect(() => {
    setFirst((current) => (current < total ? current : 0));
  }, [total]);

  /** RN004 — busca e filtros compõem um único critério: qualquer mudança volta à página 1. */
  function handleSearch(term: string) {
    setSearch(term);
    setFirst(0);
  }

  /** AC2 — o X esvazia a busca e preserva os filtros que seguem aplicados. */
  function handleClearSearch() {
    handleSearch("");
  }

  /** AC3 — os critérios do painel entram em conjunto e a listagem volta à página 1. */
  function handleFilters(value: FilterPanelValue) {
    setFilters(
      EMBARCACAO_FILTER_COLUMNS.reduce<EmbarcacoesFilters>(
        (next, column) => ({ ...next, [column]: value[column] ?? [] }),
        { ...EMPTY_EMBARCACOES_FILTERS },
      ),
    );
    setFirst(0);
  }

  /** AC3 — devolve a consulta sem nenhum critério de filtragem; a busca permanece. */
  function handleClearFilters() {
    setFilters(EMPTY_EMBARCACOES_FILTERS);
    setFirst(0);
  }

  /** Trocar de página ou de tamanho mantém a busca e os filtros aplicados. */
  function handlePage(event: { first: number; rows: number }) {
    setFirst(event.first);
    setRows(event.rows);
  }

  /**
   * Ordenação pelo cabeçalho no ciclo crescente → decrescente → sem ordenação.
   * O terceiro clique devolve a listagem à ordem padrão da tela (Acrônimo, A-Z).
   */
  function handleSort(event: DataTableStateEvent) {
    setSort(
      event.sortOrder && isEmbarcacoesSortField(event.sortField)
        ? { field: event.sortField, direction: event.sortOrder === -1 ? "desc" : "asc" }
        : null,
    );
    setFirst(0);
  }

  function handleOpenSettings() {
    configPanel.current?.hide();
    setIsSettingsOpen(true);
  }

  /** A escolha entra em vigor em Aplicar; a densidade acompanha o usuário entre visitas. */
  function handleApplySettings(applied: EmbarcacoesTableSettings) {
    setSettings(applied);
    persistDensity(applied.density);
    setIsSettingsOpen(false);

    toast.current?.show({
      severity: "success",
      summary: "Sucesso",
      detail: TABLE_SETTINGS_APPLIED_MESSAGE,
      life: 3000,
      closable: true,
    });
  }

  /**
   * Abre o detalhamento sobre a listagem: sem troca de rota, a busca, os
   * filtros, a ordenação, a página e a densidade em vigor seguem intactos (AC1).
   */
  function handleDetail(embarcacao: Embarcacao) {
    detailTrigger.current = document.activeElement as HTMLElement | null;
    setDetailId(embarcacao.id);
  }

  /** Fechar devolve o usuário à listagem, com o foco de volta na lupa (AC1). */
  function handleCloseDetail() {
    setDetailId(null);
    detailTrigger.current?.focus();
    detailTrigger.current = null;
  }

  /** 401 no detalhamento: mesma reautenticação do interceptor do HttpClient. */
  function handleReauthenticate() {
    clearAuthToken();
    navigate(Paths.LOGIN, { replace: true });
  }

  /** Só a coluna ordenada exibe a seta, na direção em vigor. */
  function renderSortIcon({
    sorted,
    sortOrder: order,
  }: {
    sorted?: boolean;
    sortOrder?: number | null;
  }) {
    if (!sorted || !order) return null;

    const ascending = order > 0;

    return (
      <i
        className={`pi ${ascending ? "pi-sort-amount-up-alt" : "pi-sort-amount-down"} ${styles.sortIcon}`}
        data-testid={`embarcacoes-sort-${ascending ? "asc" : "desc"}`}
        aria-hidden
      />
    );
  }

  function renderActions(embarcacao: Embarcacao) {
    return <EmbarcacaoRowActions embarcacao={embarcacao} onDetail={handleDetail} />;
  }

  // AC7 — sem o privilégio `hub.embarcacoes.087` a API devolve 403 e a tela
  // apresenta o estado de "sem permissão" no lugar da tabela.
  if (status === "forbidden") {
    return (
      <section className={styles.container} data-full-bleed data-testid="embarcacoes-screen">
        <header className={styles.header}>
          <h1 className={styles.title}>{EMBARCACOES_TITLE}</h1>
        </header>

        <StateMessage
          icon="pi pi-lock"
          title="Acesso não autorizado"
          description={errorMessage ?? undefined}
          testId="embarcacoes-forbidden"
        />
      </section>
    );
  }

  return (
    <section className={styles.container} data-full-bleed data-testid="embarcacoes-screen">
      <Toast ref={toast} position="top-right" />

      <header className={styles.header}>
        <h1 className={styles.title}>{EMBARCACOES_TITLE}</h1>
      </header>

      <div className={styles.toolbar} data-testid="embarcacoes-toolbar">
        <div className={styles.filtersArea}>
          <div className={styles.searchField}>
            <label className={styles.srOnly} htmlFor="embarcacoes-search">
              {SEARCH_PLACEHOLDER}
            </label>
            <InputText
              id="embarcacoes-search"
              className={styles.searchInput}
              value={search}
              placeholder={SEARCH_PLACEHOLDER}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                handleSearch(event.currentTarget.value)
              }
              data-testid="embarcacoes-search"
            />
            <i className={`pi pi-search ${styles.searchIcon}`} aria-hidden />
          </div>

          <FilterPanel
            columns={filterColumns}
            value={filters}
            onChange={handleFilters}
            testId="embarcacoes-filters"
          />
        </div>

        <div className={styles.toolbarActions}>
          <Button
            type="button"
            className={styles.configButton}
            icon="pi pi-ellipsis-v"
            aria-label="Opções da tabela"
            aria-haspopup
            onClick={(event) => configPanel.current?.toggle(event)}
            data-testid="embarcacoes-table-config"
            severity="secondary"
            outlined
          />

          <OverlayPanel ref={configPanel} className={styles.configPanel} dismissable>
            <div className={styles.configBody} data-testid="embarcacoes-table-config-panel">
              {/* Ponto de entrada da exportação; a execução é história própria (AC4). */}
              <button
                type="button"
                className={styles.configAction}
                title={EXPORT_UNAVAILABLE_TITLE}
                disabled
                data-testid="embarcacoes-export-view"
              >
                <i className="pi pi-download" aria-hidden />
                Exportar Visualização
              </button>

              <button
                type="button"
                className={styles.configAction}
                aria-haspopup="dialog"
                onClick={handleOpenSettings}
                data-testid="embarcacoes-table-settings-toggle"
              >
                <i className="pi pi-cog" aria-hidden />
                Configurações da Tabela
              </button>
            </div>
          </OverlayPanel>
        </div>
      </div>

      {isSettingsOpen ? (
        <TableSettingsDialog
          settings={settings}
          onApply={handleApplySettings}
          onCancel={() => setIsSettingsOpen(false)}
        />
      ) : null}

      {/* A listagem permanece montada por trás do modal (AC1). */}
      {detailId !== null ? (
        <EmbarcacaoDetailDialog
          embarcacaoId={detailId}
          onClose={handleCloseDetail}
          onReauthenticate={handleReauthenticate}
        />
      ) : null}

      {status === "error" ? (
        <StateMessage
          icon="pi pi-exclamation-triangle"
          title="Não foi possível carregar as embarcações."
          description={errorMessage ?? undefined}
          testId="embarcacoes-error"
        >
          <Button
            type="button"
            className={styles.retryButton}
            label="Tentar novamente"
            icon="pi pi-refresh"
            onClick={reload}
            data-testid="embarcacoes-retry"
          />
        </StateMessage>
      ) : (
        <div className={styles.tableWrapper}>
          {isLoading ? (
            <span className={styles.srOnly} role="status" data-testid="embarcacoes-loading">
              Carregando embarcações...
            </span>
          ) : null}

          {isEmpty ? (
            <EmptyState
              kind={emptyKind}
              content={emptyContent}
              onClear={
                emptyKind === "search"
                  ? handleClearSearch
                  : emptyKind === "filter"
                    ? handleClearFilters
                    : undefined
              }
              testId="embarcacoes-empty"
            />
          ) : (
            <DataTable
              className={styles.table}
              value={embarcacoes}
              dataKey="id"
              loading={isLoading}
              size={settings.density === "compacto" ? "small" : "normal"}
              tableStyle={{ minWidth: "48rem" }}
              emptyMessage={emptyContent.title}
              lazy
              totalRecords={total}
              paginator={total > 0}
              paginatorClassName={styles.paginator}
              paginatorTemplate={PAGINATOR_TEMPLATE}
              currentPageReportTemplate={CURRENT_PAGE_REPORT}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              first={first}
              rows={rows}
              onPage={handlePage}
              sortField={sort?.field}
              sortOrder={sortOrder}
              onSort={handleSort}
              sortIcon={renderSortIcon}
              // Habilita o terceiro estado do ciclo: sem ordenação.
              removableSort
              aria-label="Embarcações"
              data-testid="embarcacoes-table"
            >
              <Column
                field={SORT_FIELD_BY_COLUMN.acronimo}
                header="Acrônimo"
                style={COLUMN_WIDTHS.acronimo}
                sortable
              />
              <Column
                field={SORT_FIELD_BY_COLUMN.nome}
                header="Embarcação"
                style={COLUMN_WIDTHS.nome}
                sortable
              />
              <Column
                field={SORT_FIELD_BY_COLUMN.tipo}
                header="Tipo"
                style={COLUMN_WIDTHS.tipo}
                sortable
              />
              <Column
                field={SORT_FIELD_BY_COLUMN.situacao}
                header="Situação"
                style={COLUMN_WIDTHS.situacao}
                sortable
              />
              <Column
                // Coluna de ações sem rótulo: a lupa já identifica o comando.
                header=""
                headerClassName={styles.actionsHeader}
                bodyClassName={styles.actionsCell}
                style={COLUMN_WIDTHS.acoes}
                body={renderActions}
              />
            </DataTable>
          )}
        </div>
      )}
    </section>
  );
}
